import { defineStore } from "pinia";
import { UserModel } from "~/models/User.model";

export const useAuthStore = defineStore({
  id: "auth-store",
  state: () => {
    return {
      user: new UserModel({}) as UserModel,
      redirectTo: "",
      $authService: useNuxtApp().$authService,
      authenticationRequired: false,
      // True once getUser() has settled, either way (resolved with a real
      // user or failed to a guest). shared-ui's MgHeaderAvatarToggle reads
      // this to show a neutral skeleton instead of the guest icon while the
      // auth check is still in flight, so a signed-in user's real avatar
      // never gets preceded by a confidently-wrong "logged out" flash.
      initialized: false,
      _requirePasswordConfirmation: false,
      _missingStep: "",
      isCommingFrom: "",
      _systemId: "",
      _lastPasswordCheckTimestamp: 0,
      showOnline: false,
      statusLoaded: false,
    };
  },
  actions: {
    async getUser(): Promise<UserModel> {
      if (this.user.id) return this.user;
      try {
        const { data } = await this.$authService.getAuthenticatedUser();
        this.user = new UserModel(data.data);
        // shared-ui's MgHeaderUIUser reads user.coins (account dropdown coins
        // row/XP bar). Fill it from mgc_balance only when the API response
        // didn't already provide a real coins value — defensive, matching
        // agency-frontend/tv-frontend's stores/auth.ts.
        if (typeof this.user.coins !== "number" && typeof this.user.mgc_balance === "number") {
          this.user.coins = this.user.mgc_balance;
        }
      } catch {
        this.user = new UserModel({});
      } finally {
        this.initialized = true;
      }
      return this.user;
    },
    setUser(user: UserModel) {
      this.user = user;
    },
    setRedirectTo(redirectTo: string) {
      this.redirectTo = redirectTo;
    },
    setCommingFrom(commingFrom: string) {
      this.isCommingFrom = commingFrom;
    },
    setSystemId(systemId: string) {
      this._systemId = systemId;
    },
    /**
     * Single source of truth for "which platform is this signup coming from".
     *
     * Priority: explicit ?system_id= → service inferred from redirect_to (the
     * OAuth flow nests it) → a short-lived cookie → this app's own id. The
     * cookie exists because everything above it lives in the URL or in memory,
     * and both are gone after a reload: someone who lands on /register from
     * jobs and presses F5 used to fall straight through to VITE_SYSTEM_ID and
     * be recorded as MGN. Same pattern as the `mgn_ref` and `mg_city_session`
     * fallbacks already used on step 1.
     *
     * Only written when the value came from a real signal, never from the
     * fallback, and short-lived so it cannot mis-attribute a later visit.
     */
    resolveSystemId(explicit: string): string {
      const cookie = useCookie<string | null>("mgn_system_id", {
        maxAge: 60 * 30,
        path: "/",
        sameSite: "lax",
      });

      // originSystemId is never empty: commingFrom falls back to "network", which
      // maps to 4. Reading it unconditionally would make every query-less load
      // look like a real MGN signal — the cookie would never be read, and worse,
      // it would be overwritten with 4. So only trust it when the URL actually
      // carries something to derive from.
      // Read the query straight off location, not only from the caller: these
      // pages are prerendered, and useRoute().query is still empty when their
      // setup runs on the client, so the explicit argument arrives blank on a
      // first load. commingFrom already reads location.search for the same
      // reason.
      const search = typeof location !== "undefined" ? location.search : "";
      const params = new URLSearchParams(search);
      const fromUrl = explicit || params.get("system_id") || "";
      const hasOriginSignal = Boolean(params.get("from") || params.get("redirect_to"));
      const derived = fromUrl || (hasOriginSignal ? this.originSystemId : "") || "";

      if (derived) {
        cookie.value = derived;
      }
      const systemId = derived || cookie.value || import.meta.env.VITE_SYSTEM_ID || "";
      this._systemId = systemId;

      return systemId;
    },
    setAuthenticationRequired(authenticationRequired: boolean) {
      this.authenticationRequired = authenticationRequired;
    },
    setRequirePasswordConfirmation(requirePasswordConfirmation: boolean) {
      this._requirePasswordConfirmation = requirePasswordConfirmation;
    },
    setMissingStep(missingStep: string) {
      this._missingStep = missingStep;
    },
    setLastPasswordCheckTimestamp(timestamp: number) {
      this._lastPasswordCheckTimestamp = timestamp;
    },
    // The show-online toggle in the shared account dropdown (MgHeaderUIUser)
    // had no backend wiring on this site before — showOnline was a local,
    // unpersisted ref. The endpoint is shared platform-wide (mundo-gamer-
    // network/support's UserStatusProvider, already registered on api-main,
    // which this site's API_BASE_URL points at); this wires the store to it,
    // matching tv-frontend/jobs-frontend/etc.
    async fetchOnlineStatus() {
      if (this.statusLoaded || !this.signedIn) return;
      try {
        const { getUserStatus } = await import("../services/userStatusService");
        const response = await getUserStatus();
        this.showOnline = !response.data.data[0].user_status[0].hidden_status;
        this.statusLoaded = true;
      } catch (e: any) {
        if (e?.response?.status !== 401) {
          this.showOnline = false;
          this.statusLoaded = true;
        }
      }
    },
    async toggleOnlineStatus() {
      this.showOnline = !this.showOnline;
      const { userChangeStatus } = await import("../services/userStatusService");
      await userChangeStatus(!this.showOnline);
    },
  },
  getters: {
    userId: (state) => (state.user ? state.user.id : null),
    signedIn(): boolean {
      return !!this.user.id;
    },
    registerRoute(): string {
      const $localePath = useLocalePath();
      const base = $localePath("/register");
      // localePath may already contain ?lang=en — split it and merge with current params
      const [basePath, baseQuery] = base.split("?");
      const merged = new URLSearchParams(baseQuery || "");
      new URLSearchParams(location.search).forEach((v, k) => {
        if (!merged.has(k)) merged.set(k, v);
      });
      const qs = merged.toString();
      return qs ? `${basePath}?${qs}` : basePath;
    },
    loginRoute(): string {
      const $localePath = useLocalePath();
      const base = $localePath("/login");
      const [basePath, baseQuery] = base.split("?");
      const merged = new URLSearchParams(baseQuery || "");
      new URLSearchParams(location.search).forEach((v, k) => {
        if (!merged.has(k)) merged.set(k, v);
      });
      const qs = merged.toString();
      return qs ? `${basePath}?${qs}` : basePath;
    },
    commingFrom(): string {
      if (!this.isCommingFrom) {
        const params = new URLSearchParams(location.search);

        // 1. Explicit ?from= param (legacy)
        const from = params.get("from");
        if (from) { this.isCommingFrom = from; return from; }

        // 2. Map ?system_id= to platform slug
        const systemIdMap: Record<string, string> = {
          "1": "academy", "2": "community", "3": "jobs",
          "4": "network",  "5": "tv",        "6": "token",
          "8": "shop",    "9": "club",      "10": "agency",
          "11": "ai",     "12": "ventures",
        };
        const sid = params.get("system_id") || this._systemId;
        if (sid && systemIdMap[sid]) {
          this.isCommingFrom = systemIdMap[sid];
          return this.isCommingFrom;
        }

        // 3. Infer from redirect_to hostname
        const redirectTo = params.get("redirect_to") || this.redirectTo;
        if (redirectTo) {
          try {
            // OAuth login flow: prefer an explicit system_id nested in the authorize
            // URL (api/login forwards it) over host inference — it's the exact source.
            const nestedSid = new URL(redirectTo).searchParams.get("system_id");
            if (nestedSid && systemIdMap[nestedSid]) {
              this.isCommingFrom = systemIdMap[nestedSid];
              return this.isCommingFrom;
            }
            const host = new URL(redirectTo).hostname;
            const hostMap: Record<string, string> = {
              academy: "academy", community: "community", tv: "tv",
              agency: "agency",   jobs: "jobs",           club: "club",
              token: "token",     ai: "ai",               shop: "shop",       deals: "shop",
              ventures: "ventures",
            };
            const sub = Object.keys(hostMap).find((k) => host.startsWith(k + ".") || host === k);
            if (sub) { this.isCommingFrom = hostMap[sub]; return this.isCommingFrom; }
            if (host.includes("mundogamerjobs")) { this.isCommingFrom = "jobs"; return "jobs"; }

            // 3b. OAuth login flow: redirect_to is the authorize URL on
            // api.mundogamer.network, so the outer host above resolves to "network".
            // The real originating service lives in the NESTED redirect_uri param
            // (the service's oauth-client/callback, e.g. api.mundogamerjobs.com or
            // api.mundogamer.agency). Parse it so logins/registrations triggered
            // through api-X/login still attribute to the right network instead of
            // falling back to MGN.
            const innerUri = new URL(redirectTo).searchParams.get("redirect_uri");
            if (innerUri) {
              const innerHost = new URL(innerUri).hostname;
              let innerSlug: string | null = null;
              if (innerHost.includes("mundogamerjobs")) {
                innerSlug = "jobs";
              } else {
                const tld = innerHost.match(
                  /mundogamer\.(tv|agency|academy|community|club|token|ai|ventures|shop|deals)$/,
                );
                if (tld) {
                  innerSlug = tld[1] === "deals" ? "shop" : tld[1];
                } else {
                  const innerSub = Object.keys(hostMap).find((k) => innerHost.startsWith(k + "."));
                  if (innerSub) innerSlug = hostMap[innerSub];
                }
              }
              if (innerSlug) { this.isCommingFrom = innerSlug; return innerSlug; }
            }
          } catch { /* invalid URL — ignore */ }
        }

        this.isCommingFrom = "network";
      }
      return this.isCommingFrom;
    },
    missingStep() {
      return this._missingStep;
    },
    requirePasswordConfirmation(): boolean {
      return this._requirePasswordConfirmation;
    },
    systemId(): string {
      return this._systemId;
    },
    // Numeric system_id derived from the originating service (commingFrom),
    // used to attribute the registration's origin_system when no explicit
    // ?system_id= is present (e.g. arriving via the OAuth login flow, where
    // system_id is nested inside redirect_to). Mirrors the backend system map.
    originSystemId(): string {
      const slugToId: Record<string, string> = {
        academy: "1", community: "2", jobs: "3", network: "4", tv: "5",
        token: "6", shop: "8", club: "9", agency: "10", ai: "11", ventures: "12",
      };
      return slugToId[this.commingFrom] || "";
    },
    lastPasswordCheckTimestamp(): number {
      return this._lastPasswordCheckTimestamp;
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot));
}
